import { useState, useEffect, useCallback } from 'react';

export interface CountdownItem {
  id: string;
  childName: string;
  itemName: string;
  totalSeconds: number;
  remainingSeconds: number;
  startedAt: number;
}

interface Props {
  items: CountdownItem[];
  onRemove: (id: string) => void;
  onFinish: (id: string) => void;
}

export default function CountdownFloat({ items, onRemove, onFinish }: Props) {
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    if (items.length === 0) return;
    const timer = setInterval(() => setTicks(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [items.length]);

  const getRemaining = useCallback((item: CountdownItem) => {
    const elapsed = Math.floor((Date.now() - item.startedAt) / 1000);
    return Math.max(0, item.totalSeconds - elapsed);
  }, [ticks]);

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-20 right-4 z-[100] space-y-2">
      {items.map(item => {
        const remaining = getRemaining(item);
        const pct = (remaining / item.totalSeconds) * 100;
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        const isFinished = remaining === 0;

        if (isFinished) {
          setTimeout(() => onFinish(item.id), 100);
        }

        return (
          <div key={item.id} className={`w-64 rounded-2xl overflow-hidden border-2 transition-all ${isFinished ? 'border-emerald-400 bg-emerald-50' : 'border-blue-200 bg-white'}`}>
            <div className={`px-3 py-2 flex items-center justify-between ${isFinished ? 'bg-emerald-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <i className={`${isFinished ? 'ri-check-double-fill' : 'ri-timer-fill'} text-white text-sm flex-shrink-0`}></i>
                <span className="text-white text-xs font-bold truncate">{item.childName} · {item.itemName}</span>
              </div>
              <button onClick={() => onRemove(item.id)} className="w-5 h-5 flex items-center justify-center text-white/70 hover:text-white cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            <div className="px-3 py-2">
              {isFinished ? (
                <div className="text-center">
                  <p className="text-emerald-600 font-black text-sm">时间到！</p>
                  <p className="text-emerald-500 text-xs">请结束活动</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center mb-1.5">
                    <span className="text-2xl font-black text-blue-700">{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span>
                  </div>
                  <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full transition-all duration-1000"
                      style={{ width: `${pct}%` }}></div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
